import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSidebar } from "@src/presentation/contexts/SidebarContext";
import { Button, cn } from "@heroui/react";
import { useState, useEffect } from "react";
import { ChevronRight } from "lucide-react";
import type { SidebarChild, SidebarMenuItem } from "./sidebarConfig";

const SidebarItem = ({ icon: Icon, label, route, isDisabled, children }: SidebarMenuItem) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const { expanded, isOpen, screenType, setIsOpen } = useSidebar();
  const isMobile = screenType === "mobile";
  const isActive = location.pathname === route;
  const hasChildren = children && children.length > 0;

  // Check if any child is active (exact match or starts with child route)
  const isChildActive = hasChildren && children.some((child) => {
    if (child.type === "route") {
      // Check exact match or if current path is a sub-route of the child
      return location.pathname === child.route || location.pathname.startsWith(`${child.route}/`);
    }
    return false;
  });

  // Keep menu open if a child is active or manually expanded
  const shouldShowChildren = isExpanded || isChildActive;

  // Auto-expand if a child is active
  useEffect(() => {
    if (isChildActive) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [isChildActive]);

  // Reset expanded state when location changes and no child is active
  useEffect(() => {
    if (!isChildActive) {
      setIsExpanded(false);
    }
  }, [location.pathname, isChildActive]);

  useEffect(() => {
    if (!isExpanded) {
      setIsExpanded(false);
    }
  }, []);

  const handleChildClick = (child: SidebarChild) => {
    if (child.isDisabled) return;

    if (child.type === "route") {
      if (location.pathname !== child.route) navigate(child.route);
      if (isMobile) setIsOpen(false);
    } else if (child.type === "section") {
      const section = document.getElementById(child.sectionId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start", inline: "start" });
      }
      if (isMobile) setIsOpen(false);
    }
  };

  const handleParentClick = () => {
    if (isDisabled) return;

    if (hasChildren) {
      // Toggle the expanded state for items with children
      setIsExpanded(!isExpanded);

      // If parent has a route and we're expanding (not collapsing), navigate to it
      if (route && !isExpanded) {
        navigate(route);
        if (isMobile) setIsOpen(false);
      }
    } else if (route) {
      // If parent has a route and no children, navigate to it
      navigate(route);
      if (isMobile) setIsOpen(false);
    }
  };

  return (
    <li>
      {/* Parent Item - No background change, clean look */}
      {isDisabled ? (
        <Button
          className={cn(
            "flex w-full justify-between items-center py-2.5 px-3 rounded text-sm font-medium transition-all",
            "text-muted-foreground cursor-not-allowed opacity-50"
          )}
          isDisabled={isDisabled}
        >
          <div className="flex items-center gap-3">
            <div className="opacity-60">{Icon}</div>
            {(expanded || (isMobile && isOpen)) && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {label}
              </motion.span>
            )}
          </div>
        </Button>
      ) : hasChildren ? (
        <button
          onClick={handleParentClick}
          className={cn(
            "flex w-full justify-between items-center py-2.5 px-3 rounded text-sm font-medium transition-all",
            "hover:text-foreground focus:outline-none",
            isActive ? "text-foreground" : "text-muted-foreground"
          )}
        >
          <div className="flex items-center gap-3">
            {Icon}
            {(expanded || (isMobile && isOpen)) && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {label}
              </motion.span>
            )}
          </div>
          {(expanded || (isMobile && isOpen)) && (
            <motion.div
              initial={{ rotate: 0 }}
              animate={{ rotate: shouldShowChildren ? 90 : 0 }}
              transition={{ duration: 0.2 }}
              className="text-muted-foreground"
            >
              <ChevronRight className="h-4 w-4" />
            </motion.div>
          )}
        </button>
      ) : (
        <Link
          to={route!}
          className={cn(
            "flex items-center gap-3 py-2.5 px-3 rounded text-sm font-medium transition-all",
            "hover:text-foreground",
            isActive ? "text-foreground" : "text-muted-foreground"
          )}
          onClick={() => {
            if (isMobile) {
              setIsOpen(false);
            }
          }}
        >
          {Icon}
          {(expanded || (isMobile && isOpen)) && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {label}
            </motion.span>
          )}
        </Link>
      )}

      {/* Children Items with Pinecone-style left border and subtle background */}
      {hasChildren && (expanded || (isMobile && isOpen)) && (
        <AnimatePresence>
          {shouldShowChildren && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <ul className="mt-1 ml-3 space-y-0.5 relative">
                {/* Vertical line connecting children */}
                <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />

                {children.map((child) => {
                  // Only highlight if it's an exact match
                  // Sub-routes (like /pascos/:id or /pascos/:id/edit) shouldn't highlight any child
                  const isChildItemActive =
                    child.type === "route" &&
                    location.pathname === child.route;

                  return (
                    <li key={child.label} className="relative">
                      {child.isDisabled ? (
                        <Button
                          className={cn(
                            "flex w-full justify-start items-center py-2 pl-6 pr-3 rounded text-sm transition-all",
                            "text-muted-foreground cursor-not-allowed opacity-50"
                          )}
                          isDisabled={child.isDisabled}
                          size="sm"
                        >
                          <span className="truncate">{child.label}</span>
                        </Button>
                      ) : (
                        <button
                          onClick={() => handleChildClick(child)}
                          className={cn(
                            "flex w-full justify-start items-center py-2 pl-6 pr-3 rounded text-sm transition-all relative group",
                            "text-muted-foreground hover:text-foreground focus:outline-none",
                            isChildItemActive
                              ? "bg-primary/5 text-foreground font-medium"
                              : "hover:bg-muted/50"
                          )}
                        >
                          {/* Active indicator - horizontal line from vertical bar */}
                          <div
                            className={cn(
                              "absolute left-2 top-1/2 -translate-y-1/2 w-3 h-px transition-all",
                              isChildItemActive
                                ? "bg-primary"
                                : "bg-border group-hover:bg-foreground/30"
                            )}
                          />

                          <span className="truncate">{child.label}</span>

                          {/* Active dot indicator */}
                          {isChildItemActive && (
                            <motion.div
                              initial={{ scale: 0, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className="ml-auto w-1.5 h-1.5 bg-primary rounded-full"
                            />
                          )}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </li>
  );
};

export default SidebarItem;